import moment, { Moment } from 'moment';
import { isValidArray } from 'src/Library/Helper';
import { CInvoicingChargeList } from './CInvoicingChargesList';
import { CInvoicingCreateInvoiceFormType, CreateInvoiceCharges, CreateInvoiceDTO, CreateInvoiceExchangeRate, ResolveInvoiceDTO } from './CInvoicingCreateInvoiceForm.types';
import { dueDateObj } from 'src/components/pages/invoicing/enums/terms_and_due_date_enum';
import { chargeCategoryKeyLabelMap } from 'src/components/pages/invoicing/CreateNewInvoice/constants';
import { TaxValueMap } from 'src/components/pages/invoicing/enums/tax_list.enum';
import { ApiCallObject } from '../types';

export class CInvoicingCreateInvoiceForm implements CInvoicingCreateInvoiceFormType {
  constructor(json: CInvoicingCreateInvoiceFormType) {
    Object.assign(this, json);
  }

  sourceType?: string;

  otherPartyId?: string;

  shipmentBookingId?: string;

  invoiceType?: string;

  mode?: string;

  billType?: string;

  billNumber?: string;

  invoiceNumber?: string;

  invoiceDate?: Moment;

  dueDate?: Moment;

  term?: string;

  internalReferenceNumber?: string;

  additionalComments?: string;

  createInvoiceExchangeRates?: CreateInvoiceExchangeRate[];

  createInvoiceCharges?: CreateInvoiceCharges;

  invoiceAmount?: number;

  invoiceCurrency?: string;

  gstAmount?: number;

  gstCurrency?: string;

  teams?: string[];

  toInvoicingChargeList = (): CInvoicingChargeList => {
    const charges = this.createInvoiceCharges ?
      Object.values(this.createInvoiceCharges)
        .filter(e => !!e)
        .reduce((acc, list) => {
          if (isValidArray(list)) {
            acc = [...acc, ...list];
          }
          return acc;
        }, [])
      : [];
    return new CInvoicingChargeList({
      charges,
    });
  };

  toUniqueCurrenciesInCharges = (): string[] => {
    const currencies = this.toInvoicingChargeList()?.charges.reduce((acc, charge) => {
      const { currency } = charge || {};
      if (currency && acc.indexOf(currency) === -1) {
        acc.push(currency);
      }
      return acc;
    }, [] as string[]);
    return currencies;
  };

  toChargeCurrenciesNotInExchangeRates = (args: {
    invoicingCurrency?: string;
  }): string[] => {
    const { invoicingCurrency } = args;
    const newCurrencies = [] as string[];
    this.toUniqueCurrenciesInCharges()?.map((currencyToEnsure) => {
      if (currencyToEnsure && currencyToEnsure !== invoicingCurrency) {
        // Check if this currency already has an exchange rate (either as source or target)
        const hasExchangeRate = isValidArray(this.createInvoiceExchangeRates) && 
          this.createInvoiceExchangeRates?.some((exchangeRate) => {
            if (!exchangeRate) return false;
            // Check if currency is sourceCurrency (exact match)
            const isSource = exchangeRate.sourceCurrency === currencyToEnsure &&
                            exchangeRate.targetCurrency === invoicingCurrency;
            // Check if currency is targetCurrency with invoicingCurrency as source (symmetric pair)
            const isTarget = exchangeRate.sourceCurrency === invoicingCurrency &&
                            exchangeRate.targetCurrency === currencyToEnsure;
            return isSource || isTarget;
          });
        if (!hasExchangeRate) {
          newCurrencies.push(currencyToEnsure);
        }
      }
    });
    return newCurrencies;
  };

  toExchangeRatesValidity = (args: {
    invoicingCurrency?: string;
  }): string | undefined => {
    const { invoicingCurrency } = args;
    const currenciesNotInExchangeRates = this.toChargeCurrenciesNotInExchangeRates({ invoicingCurrency });
    if (currenciesNotInExchangeRates?.length) {
      const errorMessage =
        'Please provide exchange rates for all currency. Missing exchange rates for currencies  ' +
        currenciesNotInExchangeRates.join(', ');
      return errorMessage;
    }
    const sameCurrencyPair = this.createInvoiceExchangeRates?.find((firstER, firstIdx) => {
      return this.createInvoiceExchangeRates?.find((secondER, secondIdx) => {
        if (firstIdx === secondIdx) return;

        const reflexive =
          secondER?.sourceCurrency === firstER?.sourceCurrency &&
          secondER?.targetCurrency === firstER?.targetCurrency;
        const symmetric =
          secondER?.sourceCurrency === firstER?.targetCurrency &&
          firstER?.sourceCurrency === secondER?.targetCurrency;

        return reflexive || symmetric;
      });
    });
    if (sameCurrencyPair) {
      const errorMessage =
        'Duplicate exchange rate pair: ' +
        sameCurrencyPair?.sourceCurrency +
        ' - ' +
        sameCurrencyPair?.targetCurrency;
      return errorMessage;
    }
    return;
  };

  toCreateInvoiceDTO(args: {
    err: any;
    toOrgCurrency: string;
    shipmentBookingId?: string | number;
    isShipment: boolean;
    shipmentBookingLabel?: string;
    category: string;
    fileLink: string;
    flagAcceptCharges?: boolean;
  }): ApiCallObject<CreateInvoiceDTO> {
    const {
      err,
      toOrgCurrency,
      shipmentBookingId,
      isShipment,
      shipmentBookingLabel,
      category,
      fileLink,
      flagAcceptCharges,
    } = args;
    if (err) {
      return {
        validationStatus: 'FAILED',
        errorMessage: 'Please fill all required fields',
      };
    }
    const exchangeRatesError = this.toExchangeRatesValidity({ invoicingCurrency: toOrgCurrency });
    if (exchangeRatesError) {
      return {
        validationStatus: 'FAILED',
        errorMessage: exchangeRatesError,
      };
    }

    const dueData = (() => {
      if (this.dueDate) {
        return moment(this.dueDate);
      } else {
        const invoiceDataClone = this.invoiceDate?.clone();
        if (this.term) {
          return invoiceDataClone?.add(dueDateObj[this.term], 'days');
        }
        return invoiceDataClone;
      }
    })();

    const shipmentId = shipmentBookingId ? parseInt(shipmentBookingId as string) : undefined;
    const body: CreateInvoiceDTO = {
      ...(isShipment ? {
        shipmentId,
        shipmentNumber: shipmentBookingLabel,
      } : {
          bookingId: shipmentId,
          bookingNumber: shipmentBookingLabel,
        }),
      category,
      fileLink,
      teamIds: this.teams?.map(team =>  Number(team)),
      // dueDate:
      //   moment(this.dueDate || this.invoiceDate).valueOf()?.toString(),
      dueDate: dueData?.valueOf().toString() || '',
      toOrganisationId: this.otherPartyId || '',
      type: this.invoiceType || '',
      billType: this.billType,
      billNumber: this.billNumber,
      invoiceNumber: this.invoiceNumber || '',
      terms: this.term || '',
      invoiceDate: moment(this.invoiceDate).valueOf().toString(),
      internalReferenceNumber: this.internalReferenceNumber,
      invoiceAmount: flagAcceptCharges ? undefined : Number(this.invoiceAmount),
      currency: (flagAcceptCharges ? toOrgCurrency : this.invoiceCurrency) || '', // TODO: must be org invoicing currency or org currency, if charges
      tax: flagAcceptCharges ? undefined : Number(this.gstAmount),
      exchangeRates: this?.createInvoiceExchangeRates?.map(rate => ({
        fromCurrency: rate?.sourceCurrency || '',
        toCurrency: rate?.targetCurrency || '',
        rate: rate?.targetAmount ? (rate?.targetAmount / rate?.sourceAmount) : 0,
      })).filter(rate => !!rate),
      charges: typeof this.createInvoiceCharges === 'object' ? Object.keys(this.createInvoiceCharges)?.reduce((acc, categoryKey) => {
        const category = this.createInvoiceCharges?.[categoryKey];
        const categoryCharges = category?.filter(charge => charge?.name)?.map(charge => ({
          id: charge?.id ? parseInt(charge?.id) : undefined,
          amount: charge?.price,
          chargeCategory: chargeCategoryKeyLabelMap[categoryKey], // charge?.extraData?.name,
          chargeName: charge?.name,
          hsnCode: charge?.hsnCode,
          currency: charge?.currency,
          totalUnits: charge?.unitCount,
          unitType: charge?.unitType,
          taxName: charge?.tax,
          containerType: charge?.containerType,
          containerSize: charge?.containerSize,
          taxPercentage: TaxValueMap[charge?.tax],
        }));
        acc.push(...categoryCharges);
        return acc;
      }, [] as any[]).filter(charge => !!charge) : [],
      remarks: this.additionalComments,
      mode: this.mode || '',
    };
    // remove extra exchange rates that are not needed
    if (body?.exchangeRates?.length) {
      const chargeCurrencies: Set<String> = new Set();
      body?.charges?.forEach(charge => charge?.currency && chargeCurrencies.add(charge.currency));
      body.exchangeRates = chargeCurrencies.size ? body.exchangeRates.filter(rate => chargeCurrencies.has(rate.fromCurrency)) : [];
    }
    return {
      body,
      validationStatus: 'SUCCESS',
    };
  }

  toResolveInvoiceDTO(args: {
    err: any;
    toOrgCurrency: string;
    shipmentBookingId?: string | number;
    isShipment: boolean;
    shipmentBookingLabel?: string;
    category: string;
    fileLink: string;
    flagAcceptCharges?: boolean;
  }): ApiCallObject<ResolveInvoiceDTO> {
    return this.toCreateInvoiceDTO(args);
  }
}
